import React, {
  useEffect,
  useRef,
  useState
} from 'react';
import { Popup } from '@progress/kendo-react-popup';
import { Container, PuzzleCheckbox } from '../../index';
import { GROUPED_CHECKBOXES_PROPS } from './Types';
import classnames from 'classnames';

/**
 * PuzzleGroupedCheckboxesDropdownList component
 * @param config
 * @param onChange
 * @param component
 * @param selected
 * @constructor
 */
const GroupedCheckboxes = ({ config, onChange, component, selected }: GROUPED_CHECKBOXES_PROPS) => {
  const [selectedItems, setSelectedItems] = useState(selected);
  const [isDirty, setIsDirty] = useState(false);
  const [isOpened, setIsOpened] = useState(false);
  const containerRef = useRef(null);

  /**
   * Getter for all items selected flag
   * @param groupName
   */
  const areAllItemsSelected = (groupName) => {
    const group = config.find(group => group.groupName === groupName);
    return group.items.every(item => selectedItems[item.value]) && group.items.length !== 0;
  };

  /**
   * Handler for single checkbox item
   * @param itemValue
   */
  const handleCheckboxChange = (itemValue) => {
    setIsDirty(true);
    setSelectedItems(prevState => ({
      ...prevState,
      [itemValue]: !prevState[itemValue]
    }));
  };

  /**
   * Handler for select all checkbox
   * @param groupName
   */
  const handleSelectAll = (groupName) => {
    setIsDirty(true);
    const group = config.find(group => group.groupName === groupName);
    const allSelected = areAllItemsSelected(groupName);

    const updatedItems = group.items.reduce((acc, item) => {
      if (!item.disabled) {
        acc[item.value] = !allSelected;
      }
      return acc;
    }, {});

    setSelectedItems(prevState => ({
      ...prevState,
      ...updatedItems
    }));
  };

  /**
   * OnBlur handler
   * @param currentTarget
   * @param relatedTarget
   */
  const onBlur = ({ currentTarget, relatedTarget }: React.FocusEvent<HTMLDivElement>) => {
    if (!currentTarget.contains(relatedTarget) && currentTarget !== relatedTarget) {
      setIsOpened(false);
      isDirty && onChange(selectedItems);
    }
  };

  /**
   * Renderer for standalone items
   * @param item
   */
  const getStandaloneJSX = (item) => {
    const isChecked = !!selectedItems[item.items[0].value];
    return (
      <div
        className={classnames('tp-selectable-item tp-standalone', {
          'tp-item-checked': isChecked
        })}
      >
        <PuzzleCheckbox
          label={item.items[0].label}
          type="checkbox"
          checked={isChecked}
          disabled={item.disabled}
          onChange={() => handleCheckboxChange(item.items[0].value)}
        />
      </div>
    );
  };

  /**
   * Renderer for grouped items
   * @param item
   */
  const getGroupedJSX = (item) => {
    const isAllChecked = areAllItemsSelected(item.groupName);
    const showSelectAll = item.items.length > 1;
    return (
      <div key={item.groupName} className={'tp-group'}>
        <h4>{item.groupName}</h4>
        {showSelectAll ? (
          <div
            className={classnames('tp-selectable-item tp-select-all', {
              'tp-item-checked': isAllChecked
            })}
          >
            <PuzzleCheckbox
              label={'Select all'}
              type="checkbox"
              checked={isAllChecked}
              onChange={() => handleSelectAll(item.groupName)}
            />
          </div>
        ) : null}
        {item.items.map((item) => {
          const isChecked = !!selectedItems[item.value];
          return (
            <div
              key={item.value}
              className={classnames('tp-selectable-item tp-group-item', {
                'tp-item-checked': isChecked
              })}
            >
              <PuzzleCheckbox
                label={item.label}
                type="checkbox"
                checked={isChecked}
                disabled={item.disabled}
                onChange={() => handleCheckboxChange(item.value)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  /**
   * Rendered for separator
   */
  const getSeparatorJSX = () => (
    <hr 
      className="tp-puzzle-grouped-checkbox-separator"
    />);

  /**
   * Open list handler
   */
  const openList = () => {
    if (!isOpened) {
      setSelectedItems(selected);
      setIsDirty(false);
      setIsOpened(prev => !prev);
    }
  };

  return (
    <Container
      displayInlineFlex
      className={'tp-grouped-checkboxes'}
      ref={containerRef}
    >
      {<div onClick={openList}>
        {component}
      </div>}
      <Popup
        anchor={containerRef.current}
        anchorAlign={{ horizontal: 'right', vertical: 'bottom' }}
        popupAlign={{ horizontal: 'right', vertical: 'top' }}
        popupClass={'tp-grouped-checkboxes-popup'}
        show={isOpened}
      >
        <PopupContent onBlur={onBlur}>
          {config.map((dataItem) => {
            if (dataItem.isStandalone) {
              return getStandaloneJSX(dataItem);
            } else if (dataItem.isSeparator) {
              return getSeparatorJSX();
            } else {
              return getGroupedJSX(dataItem);
            }
          })}
        </PopupContent>
      </Popup>
    </Container>
  );
};

/**
 * Renderer for popup list content
 * @param children
 * @param onBlur
 * @constructor
 */
const PopupContent = ({ children, onBlur }) => {
  const contentRef = useRef(null);

  useEffect(() => {
    contentRef.current.focus();
  }, []);

  return (
    <div
      tabIndex={0}
      onBlur={onBlur}
      ref={contentRef}
    >
      {children}
    </div>
  );
};
export default GroupedCheckboxes;
